import { DataValueTypeSimple } from "@vuu-ui/vuu-data-types";
import {
  VuuColumnDataType,
  VuuRowDataItemType,
} from "@vuu-ui/vuu-protocol-types";
import { KeyboardEvent, SyntheticEvent } from "react";
import {
  stringIsValidDecimal,
  stringIsValidInt,
  stringIsValidLong,
} from "./data-utils";
import {
  isValidTimeString,
  isValidTimeStringMillis,
  normaliseTimeString,
  Time,
} from "./date/date-utils";
import type { TemporalEncoding } from "./date/EpochTimestamp";
import { EpochTimestamp } from "./date/EpochTimestamp";
import {
  getTemporalInfo,
  parseTemporalInput,
  type TemporalInfo,
} from "./date/temporal";
import { getDefaultTimeZone } from "./date/time-zone";
import type { DataValueDescriptor } from "@vuu-ui/vuu-data-types";
import { isTypeDescriptor } from "./column-utils";
import { queryClosest } from "./html-utils";
import { isValidDecimalString } from "./filters/scaled-decimal-filter";
import { ExtendedFilterOptions } from "@vuu-ui/vuu-filter-types";
import {
  type ScaledDecimal,
  ScaledDecimal2,
  ScaledDecimal4,
  ScaledDecimal6,
  ScaledDecimal8,
} from "./ScaledDecimal";

export class DataValidationError extends Error {
  actualType: string;
  expectedType: string;
  constructor(message: string, expectedType: string, actualType: string) {
    super(message);
    this.actualType = actualType;
    this.expectedType = expectedType;
  }
}

/**
 * Use with the following convention:
 *
 * <FormField data-field="my-field-name">
 */
export const getFieldName = (target: EventTarget | HTMLElement): string => {
  const saltFormField = queryClosest(target, "[data-field]") as HTMLElement;
  const fieldName = saltFormField?.dataset.field;
  if (fieldName) {
    return fieldName;
  } else {
    throw Error("named form field not found");
  }
};

export type InputSource = "typeahead-suggestion" | "text-input";

export const isNumber = (
  type: string,
  value: VuuRowDataItemType,
): value is number => type === "number";

export type CommitHandler<
  E extends HTMLElement = HTMLInputElement,
  T = VuuRowDataItemType,
> = (
  evt: SyntheticEvent<E> | KeyboardEvent<E>,
  value: T,
  source?: InputSource,
) => void;

export const isValidRange = <T>([val1, val2]: [T, T]) => {
  // compare at millisecond precision, so either precision is supported
  const time1 = normaliseTimeString(val1, true);
  const time2 = normaliseTimeString(val2, true);
  if (time1 && time2) {
    return time2 > time1;
  }
  return true;
};

/**
 * Convert a pair of string values to the type appropriate for the
 * associated column or form field. Can be used when processing a string value
 * from an input used for user editing.
 *
 */
export function getTypedRange(
  [value1, value2]: [string, string],
  dataType: VuuColumnDataType | DataValueTypeSimple,
  options?: ExtendedFilterOptions,
) {
  return [
    getTypedValue(value1, dataType, false, options),
    getTypedValue(value2, dataType, false, options),
  ];
}

const ScaledDecimalFactory = {
  scaleddecimal2: ScaledDecimal2,
  scaleddecimal4: ScaledDecimal4,
  scaleddecimal6: ScaledDecimal6,
  scaleddecimal8: ScaledDecimal8,
};

const getActualType = (
  value: string | number | boolean,
): "string" | "number" | "boolean" => {
  if (typeof value === "boolean") {
    return "boolean";
  } else if (typeof value === "number") {
    return "number";
  } else if (typeof value === "string") {
    return "string";
  }
  return "string";
};

/**
 * Convert a string value to the type appropriate for the associated
 * column or form field. Can be used when processing a string value
 * from an input used for user editing.
 *
 * @param value
 * @param type
 * @param throwIfInvalid
 */
export function getTypedValue(
  value: string,
  type: VuuColumnDataType | DataValueTypeSimple,
  throwIfInvalid?: false,
  options?: ExtendedFilterOptions,
): VuuRowDataItemType | undefined;
export function getTypedValue(
  value: string,
  type: VuuColumnDataType | DataValueTypeSimple,
  throwIfInvalid: true,
  options?: ExtendedFilterOptions,
): VuuRowDataItemType;
export function getTypedValue(
  value: string,
  type: VuuColumnDataType | DataValueTypeSimple,
  throwIfInvalid = false,
  options?: ExtendedFilterOptions,
): VuuRowDataItemType | ScaledDecimal | bigint | undefined {
  switch (type) {
    case "int": {
      if (stringIsValidInt(value)) {
        return parseInt(value, 10);
      } else if (throwIfInvalid) {
        throw new DataValidationError(
          `value ${value} is not a valid ${type}`,
          "whole number",
          getActualType(value),
        );
      } else {
        return undefined;
      }
    }

    case "long": {
      if (stringIsValidLong(value)) {
        return value;
      } else if (throwIfInvalid) {
        throw new DataValidationError(
          `value ${value} is not a valid ${type}`,
          "whole number",
          getActualType(value),
        );
      } else {
        return undefined;
      }
    }

    case "char": {
      if (value.length === 1) {
        return value;
      } else if (throwIfInvalid) {
        throw new DataValidationError(
          `value ${value} is not a valid ${type}`,
          "single character",
          getActualType(value),
        );
      } else {
        return undefined;
      }
    }

    case "scaleddecimal2":
    case "scaleddecimal4":
    case "scaleddecimal6":
    case "scaleddecimal8": {
      // An empty value is a valid (null) scaled decimal
      if (value === "" || isValidDecimalString(value)) {
        return ScaledDecimalFactory[type](value);
      } else if (throwIfInvalid) {
        throw new DataValidationError(
          `value ${value} is not a valid ${type}`,
          "decimal",
          getActualType(value),
        );
      } else {
        return undefined;
      }
    }
    case "double":
    case "number": {
      if (stringIsValidDecimal(value)) {
        return parseFloat(value);
      } else if (throwIfInvalid) {
        throw new DataValidationError(
          `value ${value} is not a valid ${type}`,
          "decimal",
          getActualType(value),
        );
      } else {
        return undefined;
      }
    }

    case "boolean":
      return value === "true" ? true : false;

    case "epochtimestamp":
    case "epochtimestampnano":
    case "date/time":
    case "date": {
      const encoding: TemporalEncoding =
        type === "epochtimestampnano" ? "epochNanos" : "epochMillis";
      const timestamp = parseTemporalInput(value, {
        kind: type === "date" ? "date" : "datetime",
        encoding,
        precision: encoding === "epochNanos" ? "ns" : "ms",
        timeZone: getDefaultTimeZone(),
      });
      if (timestamp) {
        return timestamp.toWire(encoding);
      } else if (throwIfInvalid) {
        throw new DataValidationError(
          `value ${value} is not a valid ${type}`,
          "date/time",
          getActualType(value),
        );
      } else {
        return undefined;
      }
    }

    case "time":
      if (isValidTimeString(value) || isValidTimeStringMillis(value)) {
        // We don't manipulate the values of 'extended' filters, the
        // ExtendedFilter impementation will do that.
        if (options?.type === "TimeString") {
          return value;
        } else {
          return +Time(value).asDate();
        }
      } else if (value.length > 0 && Time.isDateInMillis(value)) {
        //if value previously converted
        return Number(value);
      } else if (throwIfInvalid) {
        throw Error(`value ${value} is not a valid ${type}`);
      } else {
        return undefined;
      }
    default:
      return value;
  }
}

/**
 * Convert a string value (from user input or a filter control) to the wire
 * representation of a temporal value, using the column's TemporalInfo.
 * - time kind: a TimeString is applied to today's date (in the column time zone)
 * - any kind: canonical input (see parseTemporalInput) or raw epoch digits
 * Values are returned in the column encoding (number for epochMillis, string
 * for epochNanos).
 */
export function getTypedTemporalValue(
  value: string,
  temporalInfo: TemporalInfo,
  throwIfInvalid = false,
  baseValue?: EpochTimestamp,
): number | string | undefined {
  if (value === "") {
    return undefined;
  }
  const timestamp = parseTemporalInput(value, temporalInfo, baseValue);
  if (timestamp) {
    return timestamp.toWire(temporalInfo.encoding);
  } else if (throwIfInvalid) {
    throw new DataValidationError(
      `value ${value} is not a valid ${temporalInfo.kind}`,
      temporalInfo.kind,
      getActualType(value),
    );
  }
}

/**
 * Like getTypedValue, but takes a column (or form field) descriptor. Temporal
 * values are handled according to getTemporalInfo, other values according to
 * the type name, if there is one, otherwise the serverDataType.
 */
export function getTypedValueForDescriptor(
  value: string,
  descriptor: DataValueDescriptor,
  throwIfInvalid = false,
): VuuRowDataItemType | ScaledDecimal | bigint | undefined {
  const temporalInfo = getTemporalInfo(descriptor);
  if (temporalInfo) {
    return getTypedTemporalValue(value, temporalInfo, throwIfInvalid);
  }
  const { serverDataType = "string", type } = descriptor;
  const dataType = isTypeDescriptor(type)
    ? type.name
    : (type ?? serverDataType);
  return throwIfInvalid
    ? getTypedValue(value, dataType, true)
    : getTypedValue(value, dataType, false);
}
